import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Trash2, Plus, Grid3x3, Sparkles, Palette, Ruler, Layers } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdvancedVariantsEditor
 * - Manages axes (attributes) with options
 * - Generates matrix (cartesian) of combinations
 * - Each combo has: price_delta, stock, image_url
 * value: { axes: [{name,type,options:[...]}], combos: [{key, values:{Talla:'M', Color:'Rojo'}, price_delta, stock, image_url}] }
 */
export function AdvancedVariantsEditor({ value, onChange, categoryKey, testId = 'variants-editor' }) {
    const v = value || { axes: [], combos: [] };
    const [template, setTemplate] = useState(null);

    useEffect(() => {
        if (!categoryKey) return;
        api.get(`/variant-templates/${encodeURIComponent(categoryKey)}`).then(r => setTemplate(r.data.template)).catch(() => setTemplate(null));
    }, [categoryKey]);

    const applyTemplate = () => {
        if (!template) return;
        onChange({
            axes: template.map(a => ({ name: a.name, type: a.type, options: [...a.options] })),
            combos: [],
        });
        toast.success('Template aplicado');
    };

    const addAxis = () => {
        onChange({ ...v, axes: [...v.axes, { name: '', type: 'text', options: [] }] });
    };
    const removeAxis = (i) => {
        const axes = v.axes.filter((_, idx) => idx !== i);
        onChange({ axes, combos: [] });  // reset combos when axes change
    };
    const setAxis = (i, patch) => {
        const axes = v.axes.map((a, idx) => idx === i ? { ...a, ...patch } : a);
        onChange({ ...v, axes });
    };
    const addOption = (i, opt) => {
        if (!opt || !opt.trim()) return;
        const axes = v.axes.map((a, idx) => idx === i ? { ...a, options: [...(a.options || []), opt.trim()] } : a);
        onChange({ ...v, axes });
    };
    const removeOption = (i, oi) => {
        const axes = v.axes.map((a, idx) => idx === i ? { ...a, options: a.options.filter((_, x) => x !== oi) } : a);
        onChange({ ...v, axes, combos: v.combos.filter(c => !Object.entries(c.values || {}).some(([k, val]) => k === axes[i]?.name && val === (v.axes[i]?.options || [])[oi])) });
    };

    const combos = useMemo(() => {
        // Cartesian product of axis options
        if (!v.axes || v.axes.length === 0) return [];
        const validAxes = v.axes.filter(a => a.name && (a.options || []).length > 0);
        if (validAxes.length === 0) return [];
        let cur = [{}];
        for (const ax of validAxes) {
            const next = [];
            for (const c of cur) {
                for (const o of ax.options) {
                    next.push({ ...c, [ax.name]: o });
                }
            }
            cur = next;
        }
        return cur.map(values => {
            const key = Object.entries(values).map(([k, val]) => `${k}=${val}`).join('|');
            const existing = (v.combos || []).find(x => x.key === key);
            return existing || { key, values, price_delta: 0, stock: 0, image_url: '' };
        });
    }, [v.axes, v.combos]);

    useEffect(() => {
        // Sync combos derived state back to parent if it's changed
        if (v.axes && v.axes.length > 0) {
            const derivedKeys = new Set(combos.map(c => c.key));
            const filtered = (v.combos || []).filter(c => derivedKeys.has(c.key));
            const missing = combos.filter(c => !filtered.some(x => x.key === c.key));
            if (missing.length > 0 || filtered.length !== (v.combos || []).length) {
                onChange({ ...v, combos: [...filtered, ...missing] });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [combos.length]);

    const setComboField = (key, patch) => {
        const combos = (v.combos || []).map(c => c.key === key ? { ...c, ...patch } : c);
        onChange({ ...v, combos });
    };

    return (
        <div className="space-y-4" data-testid={testId}>
            <div className="rounded-2xl border bg-card p-3 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm font-semibold flex items-center gap-1"><Layers className="h-4 w-4 text-red-600" />Atributos (ejes)</div>
                    <div className="flex gap-2">
                        {template && <Button size="sm" variant="outline" onClick={applyTemplate} data-testid="apply-template"><Sparkles className="h-3.5 w-3.5 mr-1" />Aplicar template</Button>}
                        <Button size="sm" onClick={addAxis} className="bg-red-600 hover:bg-red-700" data-testid="add-axis"><Plus className="h-3.5 w-3.5 mr-1" />Añadir atributo</Button>
                    </div>
                </div>

                {v.axes.length === 0 && (
                    <div className="text-xs text-muted-foreground py-4 text-center">
                        No hay atributos. Añade atributos como Talla, Color, Capacidad, etc.
                        {template && <div className="mt-1">Categoría detectada: <b>{categoryKey}</b> — puedes aplicar el template.</div>}
                    </div>
                )}

                {v.axes.map((axis, i) => (
                    <AxisEditor
                        key={i}
                        axis={axis}
                        onChange={(patch) => setAxis(i, patch)}
                        onAddOption={(opt) => addOption(i, opt)}
                        onRemoveOption={(oi) => removeOption(i, oi)}
                        onRemove={() => removeAxis(i)}
                        idx={i}
                    />
                ))}
            </div>

            {combos.length > 0 && (
                <div className="rounded-2xl border bg-card p-3 space-y-2" data-testid="combo-matrix">
                    <div className="text-sm font-semibold flex items-center gap-1"><Grid3x3 className="h-4 w-4 text-red-600" />Matriz de combinaciones ({combos.length})</div>
                    <div className="text-[11px] text-muted-foreground mb-2">Cada combinación puede tener precio diferenciado (+/-), stock y foto propia.</div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left p-1.5">Combinación</th>
                                    <th className="p-1.5 w-24">Δ Precio</th>
                                    <th className="p-1.5 w-20">Stock</th>
                                    <th className="p-1.5 w-60">Imagen (URL)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combos.map((c, ix) => (
                                    <tr key={c.key} className="border-b last:border-0 hover:bg-secondary/50">
                                        <td className="p-1.5">
                                            <div className="flex flex-wrap gap-1">
                                                {Object.entries(c.values).map(([k, val]) => (
                                                    <Badge key={k} variant="outline" className="text-[10px]">{k}: {val}</Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="p-1"><Input type="number" step="0.01" value={c.price_delta || 0} onChange={(e) => setComboField(c.key, { price_delta: Number(e.target.value) })} className="h-7 text-xs" data-testid={`combo-price-${ix}`} /></td>
                                        <td className="p-1"><Input type="number" min="0" value={c.stock || 0} onChange={(e) => setComboField(c.key, { stock: Number(e.target.value) })} className="h-7 text-xs" data-testid={`combo-stock-${ix}`} /></td>
                                        <td className="p-1">
                                            <Input value={c.image_url || ''} onChange={(e) => setComboField(c.key, { image_url: e.target.value })} placeholder="https://…" className="h-7 text-xs" data-testid={`combo-img-${ix}`} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function AxisEditor({ axis, onChange, onAddOption, onRemoveOption, onRemove, idx }) {
    const [newOpt, setNewOpt] = useState('');
    const iconFor = axis.type === 'color' ? Palette : axis.type === 'size' ? Ruler : Layers;
    const Icon = iconFor;
    return (
        <div className="rounded-xl border bg-secondary/30 p-2 space-y-2" data-testid={`axis-${idx}`}>
            <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-red-600" />
                <Input value={axis.name || ''} onChange={(e) => onChange({ name: e.target.value })} placeholder="Nombre (ej. Talla)" className="h-8 flex-1" data-testid={`axis-name-${idx}`} />
                <Select value={axis.type || 'text'} onValueChange={(v) => onChange({ type: v })}>
                    <SelectTrigger className="h-8 w-24" data-testid={`axis-type-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="text">Texto</SelectItem>
                        <SelectItem value="color">Color</SelectItem>
                        <SelectItem value="size">Talla</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`axis-remove-${idx}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
            </div>
            <div className="flex flex-wrap gap-1">
                {(axis.options || []).map((o, oi) => (
                    <Badge key={oi} variant="outline" className="gap-1 pl-2 pr-1 py-0.5">
                        {o}
                        <button type="button" onClick={() => onRemoveOption(oi)} className="hover:bg-red-100 rounded p-0.5" data-testid={`opt-remove-${idx}-${oi}`}><Trash2 className="h-2.5 w-2.5 text-red-600" /></button>
                    </Badge>
                ))}
                <div className="flex items-center gap-1">
                    <Input value={newOpt} onChange={(e) => setNewOpt(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddOption(newOpt); setNewOpt(''); } }} placeholder="+ opción" className="h-6 text-xs w-24" data-testid={`opt-input-${idx}`} />
                    <Button size="sm" variant="ghost" onClick={() => { onAddOption(newOpt); setNewOpt(''); }} className="h-6 px-2" data-testid={`opt-add-${idx}`}><Plus className="h-3 w-3" /></Button>
                </div>
            </div>
        </div>
    );
}

export default AdvancedVariantsEditor;
